import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../../hooks/useAuth";
import { useOrgSnapshot } from "../../hooks/useOrgSnapshot";
import { Spinner } from "../shared/Spinner";
import type { OrgFiscal } from "../../lib/apiTyped";
import { StepName } from "./steps/StepName";
import { StepDesignPartner } from "./steps/StepDesignPartner";
import { StepFiscal, type FiscalDraft } from "./steps/StepFiscal";
import { StepPlans } from "./steps/StepPlans";
import { StepDone } from "./steps/StepDone";

export type Step = "name" | "designPartner" | "fiscal" | "plans" | "done";

const STEP_ORDER: readonly Step[] = [
  "name",
  "designPartner",
  "fiscal",
  "plans",
  "done",
] as const;

const dpSeenKey = (orgId: string) => `cowboy:onboarding-dp-seen:${orgId}`;

const transition = { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const };

const variants = {
  enter: { x: 40, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -40, opacity: 0 },
};

const reducedMotionVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

function emailPrefix(email: string | null | undefined) {
  if (!email) return "";
  return (email.split("@")[0] ?? "").replace(/[^A-Za-z0-9._-]/g, "").slice(0, 80);
}

function fiscalDraftFromOrg(
  existing: OrgFiscal | null | undefined,
  fallbackEmail: string,
): FiscalDraft {
  return {
    pais: existing?.pais ?? "ES",
    razonSocial: existing?.razonSocial ?? "",
    nif: existing?.nif ?? "",
    taxIdType: existing?.taxIdType,
    line1: existing?.direccionFiscal?.line1 ?? "",
    line2: existing?.direccionFiscal?.line2 ?? "",
    city: existing?.direccionFiscal?.city ?? "",
    postalCode: existing?.direccionFiscal?.postalCode ?? "",
    region: existing?.direccionFiscal?.region ?? "",
    emailFacturacion: existing?.emailFacturacion ?? fallbackEmail,
    consent: false,
  };
}

export function OnboardingFlow() {
  const { user, profile } = useAuth();
  const { orgId, org, loading } = useOrgSnapshot();
  const reduceMotion = useReducedMotion();

  const orgRecord = (org ?? null) as
    | (Record<string, unknown> & {
        designPartner?: boolean;
        fiscal?: OrgFiscal | null;
        fiscalConsent?: unknown;
      })
    | null;

  const designPartner = orgRecord?.designPartner === true;
  const consentRecorded = Boolean(orgRecord?.fiscalConsent);
  const fiscal = orgRecord?.fiscal ?? null;

  const initialStep = useMemo<Step>(() => {
    if (!user || !orgId) return "name";
    const display = (profile?.display_name ?? "").trim();
    const prefix = emailPrefix(user.email).toLowerCase();
    const looksLikePlaceholder =
      display.length === 0 || display.toLowerCase() === prefix;
    if (looksLikePlaceholder) return "name";
    if (designPartner && sessionStorage.getItem(dpSeenKey(orgId)) !== "1") {
      return "designPartner";
    }
    if (!fiscal) return "fiscal";
    return "plans";
    // We only want to compute this once per mount; subsequent updates are
    // driven explicitly by goNext()/goBack().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [step, setStep] = useState<Step>(initialStep);

  const [fiscalDraft, setFiscalDraft] = useState<FiscalDraft>(() =>
    fiscalDraftFromOrg(fiscal, user?.email ?? ""),
  );

  // Hydrate fiscalDraft once when org loads (handles both new orgs without
  // fiscal and existing orgs with fiscal).
  useEffect(() => {
    if (loading) return;
    setFiscalDraft((prev) => {
      // Don't clobber edits in progress: only hydrate if everything is empty.
      const isEmpty =
        !prev.razonSocial && !prev.nif && !prev.line1 && !prev.city;
      if (!isEmpty) return prev;
      return fiscalDraftFromOrg(fiscal, user?.email ?? "");
    });
  }, [loading, fiscal, user?.email]);

  const visibleSteps: Step[] = useMemo(
    () => STEP_ORDER.filter((s) => s !== "designPartner" || designPartner),
    [designPartner],
  );

  function goNext() {
    const idx = visibleSteps.indexOf(step);
    if (idx < 0 || idx >= visibleSteps.length - 1) return;
    setStep(visibleSteps[idx + 1]);
  }

  function goBack() {
    const idx = visibleSteps.indexOf(step);
    if (idx <= 0) return;
    setStep(visibleSteps[idx - 1]);
  }

  if (loading || !orgId) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const currentIdx = visibleSteps.indexOf(step);
  const motionVariants = reduceMotion ? reducedMotionVariants : variants;

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <header className="flex items-center justify-between px-6 sm:px-10 py-6">
        <div className="text-sm font-black tracking-tight text-ink">Cowboy</div>
        <div className="flex items-center gap-2" aria-label="Progreso">
          {visibleSteps.map((s, i) => (
            <span
              key={s}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === currentIdx ? 28 : 8,
                backgroundColor: i <= currentIdx ? "#0F0F0F" : "#E8E4DF",
              }}
            />
          ))}
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 sm:px-10 pb-24">
        <div className="w-full max-w-3xl">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step}
              variants={motionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
            >
              {step === "name" && (
                <StepName
                  initialName={profile?.display_name ?? ""}
                  fallbackPlaceholder={emailPrefix(user?.email)}
                  onContinue={goNext}
                />
              )}
              {step === "designPartner" && orgId && (
                <StepDesignPartner
                  orgId={orgId}
                  onContinue={() => {
                    sessionStorage.setItem(dpSeenKey(orgId), "1");
                    goNext();
                  }}
                  onBack={goBack}
                />
              )}
              {step === "fiscal" && orgId && (
                <StepFiscal
                  orgId={orgId}
                  draft={fiscalDraft}
                  onDraftChange={setFiscalDraft}
                  consentRecorded={consentRecorded}
                  onSaved={goNext}
                  onBack={goBack}
                />
              )}
              {step === "plans" && (
                <StepPlans
                  orgId={orgId}
                  orgName={(orgRecord?.name as string) ?? ""}
                  email={user?.email ?? ""}
                  currentTierSlug={(orgRecord?.activeTier as string) ?? null}
                  onContinue={goNext}
                  onBack={goBack}
                />
              )}
              {step === "done" && orgId && (
                <StepDone
                  orgId={orgId}
                  displayName={profile?.display_name ?? ""}
                  designPartner={designPartner}
                  onBack={goBack}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

function useReducedMotion() {
  const [prefers, setPrefers] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefers(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefers(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return prefers;
}
